/**
 * @module DeterministicHeadline
 * @purpose Compose a structured deterministic headline from B4 ClusterSummary fields.
 *   Used as a fallback when the Part C AI briefing is unavailable (503).
 * @depends types/reasoning.ts (ClusterSummary)
 * @dependedOnBy hooks/use-cluster-briefing.ts, components/briefing/BriefingTile.tsx
 *
 * @rules
 * - Performance: pure functions, no side effects, no React imports
 * - State: stateless
 * - Errors: n/a — inputs are typed; no throws
 */

import type { ClusterSummary } from '../types/reasoning'

export type DominantEvidenceFlag = 'execution' | 'reference' | 'presence' | 'none'

/**
 * Structured output of composeDeterministicHeadline.
 *
 * The tile renders these fields via i18n rather than using toText() directly,
 * so the structured form is the primary API.  toText() is provided for tests
 * and non-i18n contexts only.
 */
export interface DeterministicHeadline {
  directorySegment: string
  fileCount: number
  dominantFlag: DominantEvidenceFlag
  counts: { presence: number; reference: number; execution_evidence: number }
  toText: () => string
}

const EXECUTION_PRECEDENCE = 3
const REFERENCE_PRECEDENCE = 2
const PRESENCE_PRECEDENCE = 1

function resolveLastSegment(directory: string): string {
  const normalised = directory.replaceAll('\\', '/')
  const trimmed = normalised.endsWith('/') ? normalised.slice(0, -1) : normalised
  const lastSlash = trimmed.lastIndexOf('/')
  if (lastSlash === -1) return trimmed
  const segment = trimmed.slice(lastSlash + 1)
  return segment.length > 0 ? segment : trimmed
}

function pickDominantFlag(cluster: ClusterSummary): DominantEvidenceFlag {
  const flagScore = (
    cluster.hasExecutionEvidence
      ? EXECUTION_PRECEDENCE
      : cluster.hasReferenceEvidence
        ? REFERENCE_PRECEDENCE
        : cluster.hasPresenceEvidence
          ? PRESENCE_PRECEDENCE
          : 0
  )
  if (flagScore === EXECUTION_PRECEDENCE) return 'execution'
  if (flagScore === REFERENCE_PRECEDENCE) return 'reference'
  if (flagScore === PRESENCE_PRECEDENCE) return 'presence'
  return 'none'
}

/**
 * Compose a structured deterministic headline from B4 ClusterSummary fields.
 *
 * Precedence for dominant flag: execution > reference > presence > none.
 * Returns a structured DeterministicHeadline object; the tile uses i18n keys
 * to render the fields in the correct language.  toText() provides a plain-
 * English fallback for non-i18n contexts (tests, accessibility strings).
 *
 * @behaviour Pure and deterministic — same input always produces same output.
 *   Does NOT read any AI-generated fields; reads only B4 structured fields.
 * @errors n/a
 */
export function composeDeterministicHeadline(cluster: ClusterSummary): DeterministicHeadline {
  const directorySegment = resolveLastSegment(cluster.canonicalDirectory)
  const dominantFlag = pickDominantFlag(cluster)
  const counts = cluster.semanticSummary

  const toText = (): string => {
    const flagLabel =
      dominantFlag === 'none'
        ? 'no evidence flags'
        : `${dominantFlag} evidence`
    const { presence, reference, execution_evidence } = counts
    const plural = cluster.fileCount === 1 ? '' : 's'
    const filePart = `${String(cluster.fileCount)} file${plural}`
    const countPart =
      `presence ${String(presence)} / reference ${String(reference)} / execution ${String(execution_evidence)}`
    return `${directorySegment} — ${filePart}; ${flagLabel} (${countPart})`
  }

  return {
    directorySegment,
    fileCount: cluster.fileCount,
    dominantFlag,
    counts,
    toText,
  }
}
